import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import {
  Box,
  Heading,
  Text,
  Link,
  ListItem,
  OrderedList,
  UnorderedList,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';

interface MarkdownViewerProps {
  filePath: string;
  title?: string;
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ filePath, title }) => {
  const [markdown, setMarkdown] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Colors using Chakra's useColorModeValue for light/dark mode support
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('green.600', 'green.300');
  const subheadingColor = useColorModeValue('green.500', 'green.200');
  const linkColor = useColorModeValue('blue.600', 'blue.300');
  const dividerColor = useColorModeValue('gray.200', 'gray.600');
  const emphasisColor = useColorModeValue('green.700', 'green.100');
  const italicColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const fetchMarkdown = async () => {
      setIsLoading(true);
      try {
        // Normalize the file path (remove leading slash if present)
        const normalizedPath = filePath.startsWith('/')
          ? filePath.substring(1)
          : filePath;
          
        console.log('Fetching markdown from:', normalizedPath);
        const response = await fetch(normalizedPath);
        if (!response.ok) {
          throw new Error(`Failed to load markdown: ${response.status} ${response.statusText}`);
        }
        const text = await response.text();

        // Check if the content appears to be HTML (indicating an incorrect file path)
        if (text.includes('<!DOCTYPE html>') || text.includes('<html>') || text.includes('<script')) {
          throw new Error('Received HTML instead of markdown. Check file path configuration.');
        }

        setMarkdown(text);
        setError(null);
      } catch (err) {
        console.error('Error loading markdown:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkdown();
  }, [filePath]);

  // Custom component mapping for ReactMarkdown
  const components = {
    h1: (props: any) => (
      <>
        <Heading
          as="h1"
          size="xl"
          my={8}
          color={headingColor}
          textAlign="center"
          fontWeight="bold"
          letterSpacing="tight"
          {...props}
        />
        <Divider mb={8} borderColor={dividerColor} />
      </>
    ),
    h2: (props: any) => (
      <>
        <Heading
          as="h2"
          size="lg"
          mt={12}
          mb={6}
          color={headingColor}
          fontWeight="semibold"
          borderBottom="2px solid"
          borderColor={dividerColor}
          pb={2}
          textAlign="left"
          {...props}
        />
      </>
    ),
    h3: (props: any) => (
      <Heading
        as="h3"
        size="md"
        mt={8}
        mb={4}
        color={subheadingColor}
        fontWeight="semibold"
        textAlign="left"
        {...props}
      />
    ),
    h4: (props: any) => (
      <Heading
        as="h4"
        size="sm"
        mt={6}
        mb={3}
        color={subheadingColor}
        fontWeight="medium"
        fontStyle="italic"
        textAlign="left"
        {...props}
      />
    ),
    p: (props: any) => (
      <Text my={4} lineHeight="tall" fontSize="md" textAlign="left" {...props} />
    ),
    a: (props: any) => (
      <Link
        color={linkColor}
        fontWeight="medium"
        _hover={{ textDecoration: 'underline', color: linkColor }}
        isExternal={props.href && props.href.startsWith('http')}
        {...props}
      />
    ),
    ul: (props: any) => (
      <UnorderedList ml={8} my={6} spacing={3} styleType="circle" textAlign="left" {...props} />
    ),
    ol: (props: any) => (
      <OrderedList ml={8} my={6} spacing={3} textAlign="left" {...props} />
    ),
    li: (props: any) => <ListItem pb={1} textAlign="left" {...props} />,
    em: (props: any) => (
      <Text as="em" fontStyle="italic" color={italicColor} {...props} />
    ),
    strong: (props: any) => (
      <Text as="strong" fontWeight="bold" color={emphasisColor} {...props} />
    ),
    blockquote: (props: any) => (
      <Box
        as="blockquote"
        borderLeftWidth="4px"
        borderLeftColor={subheadingColor}
        pl={6}
        py={4}
        my={6}
        fontStyle="italic"
        color={italicColor}
        bg={useColorModeValue('gray.50', 'gray.700')}
        borderRadius="md"
        textAlign="left"
        {...props}
      />
    ),
    code: (props: any) => {
      if (props.inline) {
        return (
          <Box
            as="code"
            bg={useColorModeValue('gray.100', 'gray.700')}
            p={1}
            borderRadius="sm"
            fontFamily="mono"
            fontSize="0.9em"
            color={useColorModeValue('green.700', 'green.200')}
            {...props}
          />
        );
      }
      return (
        <Box
          as="pre"
          bg={useColorModeValue('gray.100', 'gray.700')}
          p={5}
          borderRadius="md"
          fontFamily="mono"
          fontSize="0.9em"
          overflowX="auto"
          my={6}
          border="1px solid"
          borderColor={borderColor}
          textAlign="left"
          {...props}
        />
      );
    },
    hr: () => <Divider my={8} borderColor={dividerColor} />,
    table: (props: any) => (
      <Box
        as="table"
        width="100%"
        my={8}
        borderWidth="1px"
        borderRadius="md"
        borderColor={borderColor}
        overflow="hidden"
        {...props}
      />
    ),
    thead: (props: any) => (
      <Box
        as="thead"
        bg={useColorModeValue('gray.50', 'gray.700')}
        borderBottomWidth="1px"
        borderColor={borderColor}
        {...props}
      />
    ),
    tbody: (props: any) => <Box as="tbody" {...props} />,
    tr: (props: any) => (
      <Box
        as="tr"
        borderBottomWidth="1px"
        borderColor={borderColor}
        _last={{ borderBottomWidth: 0 }}
        {...props}
      />
    ),
    th: (props: any) => (
      <Box
        as="th"
        p={3}
        textAlign="left"
        fontWeight="semibold"
        color={subheadingColor}
        {...props}
      />
    ),
    td: (props: any) => <Box as="td" p={3} textAlign="left" {...props} />,
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Text>Loading content...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8} textAlign="center" color="red.500">
        <Heading size="md" mb={4}>
          Error Loading Content
        </Heading>
        <Text>{error}</Text>
        <Text mt={4} fontSize="sm">
          Tried to load: {filePath}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg={bgColor}
      color={textColor}
      p={[4, 6, 10]}
      borderRadius="lg"
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
      maxWidth="900px"
      width="100%"
      mx="auto"
      my={8}
      fontSize="md"
      lineHeight="1.8"
    >
      {title && (
        <Heading
          as="h1"
          size="xl"
          mb={8}
          textAlign="center"
          color={headingColor}
          fontWeight="bold"
          letterSpacing="tight"
        >
          {title}
        </Heading>
      )}
      <Box className="markdown-body" textAlign="left">
        <ReactMarkdown
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

export default MarkdownViewer;